import CustomTooltip from '../../../../shared/CustomTooltip';
import React, { useMemo, useState } from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import UserRole from '../../../../../shared/AuthRoles';
import { theme } from '../../../../../shared/theme';
import toast from '../../../../shared/Toast';
import GuideList from '../../../Experiences/Owner/CheckinGuide/GuideList';

type Props = {
    reservation: any;
    asset: any;
    expanded: boolean;
    onToggle: () => void;
};

export default function ExperienceInfoSection({
    reservation,
    asset,
    expanded,
    onToggle,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');

    const [expandedPolicyIndex, setExpandedPolicyIndex] = useState<
        number | null
    >(null);
    const [expandedDescription, setExpandedDescription] = useState(false);
    const [expandedAmenities, setExpandedAmenities] = useState(false);

    const today = moment().startOf('day');
    const start = moment(reservation?.startDate).startOf('day');
    const end = moment(reservation?.endDate).startOf('day');
    const dayBeforeStart = start.clone().subtract(1, 'day');

    const isApproved = reservation?.status === 'approved';
    const isOnBehalf = reservation?.onBehalf !== false;

    const isCancelledOrDeclined =
        reservation?.status === 'cancelled' ||
        reservation?.status === 'declined';

    const experienceAvailable = reservation?.module?.isAvailable !== false;

    const withinWindow =
        today.isSameOrAfter(dayBeforeStart, 'day') &&
        today.isSameOrBefore(end, 'day');

    const canViewSensitiveInfo =
        isOwner ||
        (isApproved && isOnBehalf && !isCancelledOrDeclined && withinWindow);

    const labelPrefix =
        asset?.type === 'commercial' ? 'Access details' : 'Check-in';
    const isPropertyOrBoat =
        asset?.type === 'property' || asset?.type === 'boat';
    const showAddress = asset?.addressVisible !== false;

    const addressValue = useMemo(() => {
        const parts = [
            asset?.streetAddress,
            asset?.suburb,
            asset?.state,
            asset?.postcode,
        ].filter(Boolean);
        return parts.join(', ');
    }, [asset]);

    const descriptionValue = String(asset?.description || '').trim();

    const policies = useMemo(() => {
        const raw = asset?.policies ?? asset?.policy ?? [];
        const arr = Array.isArray(raw) ? raw : [raw];

        return arr
            .map((p) => {
                if (!p) return null;
                if (typeof p === 'string') return { title: 'Policy', body: p };
                return {
                    title: p.title || p.name || 'Policy',
                    body: p.description || p.body || p.text || '',
                };
            })
            .filter((p) => p && p.body && String(p.body).trim().length > 0);
    }, [asset?.policies, asset?.policy]);

    const amenities = useMemo(() => {
        const d = asset?.details || {};
        const ignoreKeys = new Set([
            'checkinDetails',
            'wifiDetails',
            'maxGuests',
            'beds',
            'bedrooms',
            'baths',
            'overnightGuests',
            'dropoffStreetAddress',
            'dropoffSuburb',
            'dropoffPostcode',
            'dropoffState',
            'dropoffCountryCode',
            'dropoffLocationSameAsPickup',
            'massages',
            'catering',
            'alcohol',
            'subType',
        ]);

        const titleize = (key: string) =>
            key
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/^./, (c) => c.toUpperCase());

        return Object.entries(d)
            .filter(([k, v]) => !ignoreKeys.has(k) && v === true)
            .map(([k]) => titleize(k))
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 60);
    }, [asset?.details]);

    const capacityItems = useMemo(() => {
        const maxGuests = Number(asset?.details?.maxGuests) || 0;
        const rooms = Number(asset?.details?.bedrooms) || 0;
        const beds = Number(asset?.details?.beds) || 0;
        const baths = Number(asset?.details?.baths) || 0;
        const overnight = Number(asset?.details?.overnightGuests) || 0;

        const items: { label: string; value: number; sub: string }[] = [];
        if (maxGuests > 0)
            items.push({
                label: 'Max guests',
                value: maxGuests,
                sub: 'People',
            });
        if (rooms > 0)
            items.push({ label: 'Rooms', value: rooms, sub: 'Rooms' });
        if (beds > 0) items.push({ label: 'Beds', value: beds, sub: 'Beds' });
        if (baths > 0)
            items.push({ label: 'Baths', value: baths, sub: 'Bathrooms' });
        if (overnight > 0)
            items.push({
                label: 'Overnight',
                value: overnight,
                sub: 'Overnight',
            });

        return items;
    }, [asset?.details]);

    const lockedText = useMemo(() => {
        if (canViewSensitiveInfo) return '';
        const unlockDate = dayBeforeStart.format('DD/MM/YYYY');
        const endDateStr = end.format('DD/MM/YYYY');
        return `Unlocks ${unlockDate} • Visible until ${endDateStr}`;
    }, [canViewSensitiveInfo, dayBeforeStart, end]);

    const descriptionPreview = useMemo(() => {
        if (!descriptionValue) return '';
        if (descriptionValue.length <= 220) return descriptionValue;
        return `${descriptionValue.slice(0, 220).trim()}…`;
    }, [descriptionValue]);

    const handleCopy = async (label: string, value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied`);
        } catch (e) {
            toast.error('Could not copy');
        }
    };

    const sectionTitleSx = {
        letterSpacing: 0.6,
        textTransform: 'uppercase' as const,
        color: 'text.secondary',
    };

    const cardSx = {
        borderRadius: 2,
        py: 1.5,
        mb: 2,
    };

    const statTileSx = {
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.neutral.main,
        borderRadius: 2,
        px: 1.25,
        py: 1,
        minHeight: 54,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        gap: 0.25,
    };

    const statValueSx = {
        color: 'text.primary',
        lineHeight: 1.1,
        fontWeight: 700,
        fontSize: 20,
        mt: 0.5,
    };

    const statLabelSx = {
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: 0.6,
        color: 'text.secondary',
        textTransform: 'uppercase' as const,
        lineHeight: 1.2,
    };

    const statSubSx = {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        lineHeight: 1.15,
    };

    const infoRowSx = {
        display: 'grid',
        gridTemplateColumns: isMobile
            ? '28px 1fr auto'
            : '28px minmax(200px, 1fr) auto',
        gap: 1,
        alignItems: 'center',
        py: 1,
        borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
    };

    const maskedValue = '••••••••••';

    return (
        <Box
            component='section'
            aria-labelledby='experience-information-heading'
            sx={{ mt: 0, borderRadius: 2, overflow: 'hidden' }}
        >
            <Box
                sx={{
                    py: 2.5,
                }}
            >
                <Typography
                    id='experience-information-heading'
                    component='h2'
                    variant='h2'
                    sx={{ letterSpacing: 0.2, color: 'text.secondary' }}
                >
                    Experience Information
                </Typography>
            </Box>
            <Box
                sx={{
                    pb: isMobile ? 2 : 3,
                    pt: 0,
                    backgroundColor: 'neutral.main',
                }}
            >
                <Divider sx={{ mt: 0, mb: 2 }} />
                {!experienceAvailable ? (
                    <Alert
                        severity='info'
                        sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}
                    >
                        This experience is no longer available
                    </Alert>
                ) : null}
                <Box sx={cardSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            mb: 0.5,
                        }}
                    >
                        <Typography variant={'h3'} sx={sectionTitleSx}>
                            Experience details
                        </Typography>

                        {!canViewSensitiveInfo ? (
                            <Chip
                                icon={<LockOutlinedIcon />}
                                label='Locked'
                                size='small'
                                variant='outlined'
                                sx={{
                                    borderColor: 'lightGrey.main',
                                    backgroundColor: 'neutral.main',
                                    borderRadius: 999,
                                    fontWeight: 800,
                                    fontSize: 10,
                                    px: 0.5,
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        ml: 0.5,
                                        color: 'text.secondary',
                                    },
                                }}
                            />
                        ) : null}
                    </Box>
                    <Box sx={{ mt: 1, borderRadius: 2, overflow: 'hidden' }}>
                        {showAddress ? (
                            <Box sx={{ ...infoRowSx, borderTop: 'none' }}>
                                <LocationOnOutlinedIcon fontSize='small' />
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        Address
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'text.secondary',
                                            wordBreak: 'break-word' as const,
                                        }}
                                    >
                                        {canViewSensitiveInfo
                                            ? addressValue || '—'
                                            : maskedValue}
                                    </Typography>
                                </Box>
                                {canViewSensitiveInfo &&
                                (addressValue || '').trim().length ? (
                                    <CustomTooltip title='Copy'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                handleCopy(
                                                    'Address',
                                                    addressValue,
                                                )
                                            }
                                        >
                                            <ContentCopyIcon fontSize='small' />
                                        </IconButton>
                                    </CustomTooltip>
                                ) : (
                                    <Box />
                                )}
                            </Box>
                        ) : null}

                        <Box sx={infoRowSx}>
                            <VpnKeyOutlinedIcon fontSize='small' />
                            <Box>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {labelPrefix}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        wordBreak: 'break-word' as const,
                                    }}
                                >
                                    {canViewSensitiveInfo
                                        ? asset?.checkinDetails || '—'
                                        : maskedValue}
                                </Typography>
                            </Box>
                            {canViewSensitiveInfo &&
                            String(asset?.checkinDetails || '').trim()
                                .length ? (
                                <CustomTooltip title='Copy'>
                                    <IconButton
                                        size='small'
                                        onClick={() =>
                                            handleCopy(
                                                labelPrefix,
                                                String(
                                                    asset?.checkinDetails || '',
                                                ),
                                            )
                                        }
                                    >
                                        <ContentCopyIcon fontSize='small' />
                                    </IconButton>
                                </CustomTooltip>
                            ) : (
                                <Box />
                            )}
                        </Box>

                        {isPropertyOrBoat ? (
                            <Box sx={infoRowSx}>
                                <WifiOutlinedIcon fontSize='small' />
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        WiFi
                                    </Typography>
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            color: 'text.secondary',
                                            wordBreak: 'break-word' as const,
                                        }}
                                    >
                                        {canViewSensitiveInfo
                                            ? asset?.details?.wifiDetails || '—'
                                            : maskedValue}
                                    </Typography>
                                </Box>
                                {canViewSensitiveInfo &&
                                String(asset?.details?.wifiDetails || '').trim()
                                    .length ? (
                                    <CustomTooltip title='Copy'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                handleCopy(
                                                    'WiFi',
                                                    String(
                                                        asset?.details
                                                            ?.wifiDetails || '',
                                                    ),
                                                )
                                            }
                                        >
                                            <ContentCopyIcon fontSize='small' />
                                        </IconButton>
                                    </CustomTooltip>
                                ) : (
                                    <Box />
                                )}
                            </Box>
                        ) : null}
                    </Box>
                    {!canViewSensitiveInfo ? (
                        <Typography
                            sx={{
                                mt: 1.25,
                                color: 'text.primary',
                                fontStyle: 'italic',
                            }}
                        >
                            Sensitive details are locked. They’ll be available
                            one day before check-in and remain accessible for
                            the duration of your experience.
                        </Typography>
                    ) : null}
                </Box>

                {canViewSensitiveInfo && asset?.guides.length > 0 && (
                    <Box sx={cardSx}>
                        <Typography variant={'h3'} sx={sectionTitleSx}>
                            Check-in Resources
                        </Typography>
                        <GuideList guides={asset?.guides} />
                    </Box>
                )}

                {descriptionValue ? (
                    <Box sx={cardSx}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                                mb: 1,
                            }}
                        >
                            <Typography variant='h3' sx={sectionTitleSx}>
                                Description
                            </Typography>

                            {descriptionValue.length > 220 ? (
                                <Button
                                    variant='text'
                                    size='small'
                                    onClick={() =>
                                        setExpandedDescription((v) => !v)
                                    }
                                    sx={{
                                        fontWeight: 700,
                                        px: 1,
                                        minWidth: 0,
                                        textTransform: 'none',
                                        fontSize: '12',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {expandedDescription
                                        ? 'Show less'
                                        : 'Read more'}
                                </Button>
                            ) : null}
                        </Box>

                        <Typography
                            sx={{
                                color: 'text.primary',
                                lineHeight: 1.5,
                            }}
                        >
                            {expandedDescription
                                ? descriptionValue
                                : descriptionPreview}
                        </Typography>
                    </Box>
                ) : null}
                {capacityItems.length > 0 || amenities.length > 0 ? (
                    <Box sx={cardSx}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                                mb: 1,
                            }}
                        >
                            <Typography variant={'h3'} sx={sectionTitleSx}>
                                Capacity and amenities
                            </Typography>

                            {amenities.length ? (
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 800,
                                        fontSize: 12,
                                    }}
                                >
                                    {amenities.length} amenities
                                </Typography>
                            ) : null}
                        </Box>

                        {capacityItems.length ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile
                                        ? 'repeat(2, 1fr)'
                                        : 'repeat(5, 1fr)',
                                    gap: 1,
                                    mb: amenities.length ? 1.25 : 0,
                                }}
                            >
                                {capacityItems.map((c) => (
                                    <Box key={c.label} sx={statTileSx}>
                                        <Typography sx={statLabelSx}>
                                            {c.label}
                                        </Typography>
                                        <Typography sx={statValueSx}>
                                            {c.value}
                                        </Typography>
                                        <Typography sx={statSubSx}>
                                            {c.sub}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : null}

                        {amenities.length ? (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    {(expandedAmenities
                                        ? amenities
                                        : amenities.slice(0, 10)
                                    ).map((a) => (
                                        <Chip
                                            key={a}
                                            label={a}
                                            size='small'
                                            variant='outlined'
                                            sx={{
                                                borderColor: 'lightGrey.main',
                                                backgroundColor: 'neutral.main',
                                                fontWeight: 700,
                                                fontSize: 14,
                                                color: 'text.primary',
                                            }}
                                        />
                                    ))}
                                </Box>

                                {amenities.length > 10 ? (
                                    <Button
                                        variant='text'
                                        size='small'
                                        onClick={() =>
                                            setExpandedAmenities((v) => !v)
                                        }
                                        sx={{
                                            fontWeight: 800,
                                            px: 1,
                                            minWidth: 0,
                                            textTransform: 'none',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {expandedAmenities
                                            ? 'Show less'
                                            : `Show ${amenities.length - 10} more`}
                                    </Button>
                                ) : null}
                            </>
                        ) : (
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.35,
                                }}
                            >
                                No amenities listed.
                            </Typography>
                        )}
                    </Box>
                ) : null}
                {policies.length ? (
                    <Box sx={cardSx}>
                        <Typography variant='body3' sx={sectionTitleSx}>
                            Policies ({policies.length})
                        </Typography>

                        <Box
                            sx={{
                                mt: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            {policies.map((p: any, idx) => {
                                const full = String(p.body || '');
                                const isLong = full.length > 160;
                                const isExpanded = expandedPolicyIndex === idx;
                                const preview = isLong
                                    ? `${full.slice(0, 160).trim()}…`
                                    : full;

                                return (
                                    <Box
                                        key={`${p.title}-${idx}`}
                                        sx={{
                                            backgroundColor: 'neutral.main',
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1.25,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography
                                                variant='body3'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {p.title}
                                            </Typography>
                                            {isLong ? (
                                                <Button
                                                    variant='text'
                                                    size='small'
                                                    onClick={() =>
                                                        setExpandedPolicyIndex(
                                                            isExpanded
                                                                ? null
                                                                : idx,
                                                        )
                                                    }
                                                    sx={{
                                                        fontWeight: 800,
                                                        px: 1,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    {isExpanded
                                                        ? 'Show less'
                                                        : 'Read more'}
                                                </Button>
                                            ) : null}
                                        </Box>
                                        <Typography
                                            variant='body3'
                                            sx={{
                                                mt: 0.75,
                                                color: 'text.primary',
                                                lineHeight: 1.5,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {isExpanded ? full : preview}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
